import React from 'react';

import type { FormattedString } from '../types';
import {
  buttonClassName,
  ButtonStyling,
  DEFAULT_VARIANT,
  DEFAULT_SIZE,
} from './styling';
import { CaretDownIcon } from '@phosphor-icons/react';
import { RCTooltip } from '../';

/* eslint-disable react/require-default-props, react/jsx-props-no-spreading */

// --- Internal interfaces ---

interface SharedProps extends Pick<ButtonStyling, 'variant' | 'size' | 'fullWidth'>{
  'aria-disabled'?: boolean;
}

interface IconOnlyProps {
  /**
   * The icon to display. Providing this prop switches the button to icon-only mode.
   * Use `leadingIcon` / `trailingIcon` for standard icon+text buttons instead.
   */
  iconOnly: JSX.Element;
  /** Required accessible label. Used as `aria-label` and default tooltip text. */
  iconLabel: string;
  /** Optional tooltip text override. Defaults to `iconLabel`. */
  children?: FormattedString;
  /** Hides the tooltip. Only use when the icon's meaning is unambiguous in context. */
  hideTooltip?: boolean;
}

interface OptionalIconProps {
  iconOnly?: never;
  /** The button text. */
  children: FormattedString;
  /** Icon displayed before the button text. */
  leadingIcon?: JSX.Element;
  /** Icon displayed after the button text. Pass `'dropdown'` for the built-in caret. */
  trailingIcon?: 'dropdown' | JSX.Element;
  iconOnlyAt?: never;
}

interface ResponsiveIconOnlyProps {
  /** Changes to icon-only at this responsive breakpoint. Use `iconLabel` for an accessible label when collapsed. */
  iconOnlyAt: 'mobile' | 'tablet';
  /** Optional when using `iconOnlyAt` — accessible label when text is hidden. */
  iconLabel: string;
}

interface ResponsiveLeadingIconOnlyProps extends ResponsiveIconOnlyProps {
  iconOnly?: never;
  /** The button text. Hidden at the `iconOnlyAt` breakpoint. */
  children: FormattedString;
  /** Required — persists as the sole visible element when collapsed. */
  leadingIcon: JSX.Element;
  /** Optional. Cannot be `'dropdown'` — a caret-only collapsed button has no meaning. */
  trailingIcon?: JSX.Element;
}

interface ResponsiveTrailingIconOnlyProps extends ResponsiveIconOnlyProps {
  iconOnly?: never;
  /** The button text. Hidden at the `iconOnlyAt` breakpoint. */
  children: FormattedString;
  /** Optional icon before the button text. */
  leadingIcon?: JSX.Element;
  /** Required — persists as the sole visible element when collapsed. Cannot be `'dropdown'`. */
  trailingIcon: JSX.Element;
}

export interface ButtonElementProps extends SharedProps {
  /** Appends `type="submit"` to the button for use inside forms. */
  isSubmit?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  id?: string;
  title?: string;
  form?: string;
}

export type LinkElementFunction = (c: React.ReactNode) => React.ReactElement;
interface LinkElementProps extends SharedProps {
  /**
   * Use with `<CoreLink />` or another routing component.
   * For example: `renderLink={c => <CoreLink to="...">{c}</CoreLink> as React.ReactElement}`
   * Note: the cast is required due to a known class-component type incompatibility in CoreLink.
   */
  renderLink: LinkElementFunction;
  /** Prevents navigation and applies visual/ARIA disabled state. Prefer over `aria-disabled` alone. */
  disabled?: boolean;
}

// Cast target for standard branches — covers both collapsible and non-collapsible cases.
interface StandardPropsWide {
  iconOnly?: never;
  children: FormattedString;
  leadingIcon?: JSX.Element;
  trailingIcon?: 'dropdown' | JSX.Element;
  iconOnlyAt?: 'mobile' | 'tablet';
  iconLabel?: string;
}

// --- Public API types ---

/**
 * Props for RCButton, which always renders a `<button>` element.
 * Use `RCButtonLink` for link-element variants, `RCButtonSpan` for span variants.
 *
 * Icon modes:
 *   - `iconOnly={<Icon />}` → icon-only; `iconLabel` required
 *   - `leadingIcon` / `trailingIcon` → standard icon+text button
 *   - `iconOnlyAt` → changes to icon-only at a responsive breakpoint;
 *     either `leadingIcon` or `trailingIcon` required (not `'dropdown'`)
 */
export type RCButtonProps =
  | (ButtonElementProps & IconOnlyProps)
  | (ButtonElementProps & OptionalIconProps)
  | (ButtonElementProps & ResponsiveLeadingIconOnlyProps)
  | (ButtonElementProps & ResponsiveTrailingIconOnlyProps);

/** Props for RCButtonLink, which renders via a link element function (e.g. CoreLink). */
export type RCButtonLinkProps =
  | (LinkElementProps & IconOnlyProps)
  | (LinkElementProps & OptionalIconProps)
  | (LinkElementProps & ResponsiveLeadingIconOnlyProps)
  | (LinkElementProps & ResponsiveTrailingIconOnlyProps);

/**
 * The button-element, standard-icon subset of RCButtonProps.
 * Use this for composition components that always render `<button>` elements
 * (e.g. RCModalActionGroup), to avoid the distributive-Omit issue with union types.
 */
export interface RCButtonStandardButtonProps extends ButtonElementProps, OptionalIconProps {}

/** Props for RCButtonSpan, which renders a `<span>` styled as a button. */
export type RCButtonSpanProps = SharedProps & OptionalIconProps;

// --- Render helpers ---

function resolveTrailingIcon(trailingIcon: 'dropdown' | JSX.Element | undefined) {
  return {
    trailingIconEl: trailingIcon !== 'dropdown' ? trailingIcon : undefined,
    iconDropdown: trailingIcon === 'dropdown',
  };
}

interface StandardContentProps {
  children: FormattedString;
  leadingIcon?: JSX.Element;
  trailingIconEl?: JSX.Element;
  iconDropdown: boolean;
  iconOnlyAt?: 'mobile' | 'tablet';
}

function StandardContent({ children, leadingIcon, trailingIconEl, iconDropdown, iconOnlyAt }: StandardContentProps) {
  return (
    <>
      {leadingIcon && <RCButtonIcon icon={leadingIcon} />}
      {iconOnlyAt ? <span className="rc-button__text">{children}</span> : children}
      {iconDropdown
        ? <span className="rc-button__caret"><CaretDownIcon weight="bold" /></span>
        : trailingIconEl && <RCButtonIcon icon={trailingIconEl} />
      }
    </>
  );
}

interface RCButtonIconProps {
  icon: JSX.Element;
}

function RCButtonIcon({ icon }: RCButtonIconProps) {
  return (
    <span className="rc-button__icon">
      {icon}
    </span>
  );
}

// --- Components ---

/**
 * Renders a `<button>` element styled as an RCButton.
 * Use `RCButtonLink` for link variants, `RCButtonSpan` for span variants.
 */
export const RCButton = React.forwardRef<HTMLButtonElement, RCButtonProps>(function RCButton(props, ref) {

    if (props.iconOnly) {
      const {
        iconOnly, iconLabel,
        children, hideTooltip = false,
        variant = DEFAULT_VARIANT, size = DEFAULT_SIZE, fullWidth,
        isSubmit, ...buttonProps
      } = props as ButtonElementProps & IconOnlyProps;
      const classes = buttonClassName({ variant, size, fullWidth, iconOnly: true });
      const buttonEl = (
        <button
          className={classes}
          type={isSubmit ? 'submit' : 'button'}
          aria-label={iconLabel}
          ref={ref}
          {...buttonProps}
        >
          <RCButtonIcon icon={iconOnly} />
        </button>
      );
      if (hideTooltip) return buttonEl;
      return (
        <RCTooltip text={children ?? iconLabel} style="buttonLabel" placement="bottom" visualOnly>
          {buttonEl}
        </RCTooltip>
      );
    }

    const {
      children, leadingIcon, trailingIcon,
      iconOnlyAt, iconLabel,
      variant = DEFAULT_VARIANT, size = DEFAULT_SIZE, fullWidth,
      isSubmit, ...buttonProps
    } = props as ButtonElementProps & StandardPropsWide;
    const { trailingIconEl, iconDropdown } = resolveTrailingIcon(trailingIcon);
    const classes = buttonClassName({
      variant, size, fullWidth,
      hasLeadingIcon: !!leadingIcon,
      hasTrailingIcon: !!trailingIconEl || iconDropdown,
      iconOnlyAt,
    });
    return (
      <button
        className={classes}
        type={isSubmit ? 'submit' : 'button'}
        aria-label={iconLabel}
        ref={ref}
        {...buttonProps}
      >
        <StandardContent
          leadingIcon={leadingIcon}
          trailingIconEl={trailingIconEl}
          iconDropdown={iconDropdown}
          iconOnlyAt={iconOnlyAt}
        >
          {children}
        </StandardContent>
      </button>
    );
  });

RCButton.displayName = 'RCButton';

/**
 * Renders a `<span>` styled as a button. Use for non-interactive placeholders such as
 * loading states or visual CTAs inside an already-linked area.
 * Use `RCButton` for interactive buttons, `RCButtonLink` for navigable links.
 */
export const RCButtonSpan = React.forwardRef<HTMLSpanElement, RCButtonSpanProps>(
  function RCButtonSpan({ children, leadingIcon, trailingIcon, variant = DEFAULT_VARIANT, size = DEFAULT_SIZE, fullWidth, ...spanProps }, ref) {
    const { trailingIconEl, iconDropdown } = resolveTrailingIcon(trailingIcon);
    const classes = buttonClassName({
      variant, size, fullWidth,
      hasLeadingIcon: !!leadingIcon,
      hasTrailingIcon: !!trailingIconEl || iconDropdown,
    });
    return (
      <span className={classes} ref={ref} {...spanProps}>
        <StandardContent
          leadingIcon={leadingIcon}
          trailingIconEl={trailingIconEl}
          iconDropdown={iconDropdown}
        >
          {children}
        </StandardContent>
      </span>
    );
  },
);

RCButtonSpan.displayName = 'RCButtonSpan';

type LinkProps = React.HTMLAttributes<HTMLAnchorElement> & {
  disabled?: boolean;
};

function buildLinkElement(
  renderLink: LinkElementFunction,
  content: React.ReactNode,
  ref: React.ForwardedRef<HTMLAnchorElement>,
  { disabled = false, ...extraProps }: LinkProps,
): React.ReactElement {
  const output = renderLink(content);

  const disabledProps = disabled ? {
    'data-disabled': true,
    'aria-disabled': true,
    onClickCapture(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
      e.preventDefault();
      e.stopPropagation();
    },
    tabIndex: -1,
  } : {};

  return React.cloneElement(output, { ...extraProps, ref, ...disabledProps });
}

/**
 * Renders a link styled as a button, via a routing component passed to `renderLink`.
 * Example: `renderLink={c => <CoreLink to="...">{c}</CoreLink>}`
 * Use `RCButton` for interactive buttons, `RCButtonSpan` for non-interactive spans.
 */
export const RCButtonLink = React.forwardRef<HTMLAnchorElement, RCButtonLinkProps>(
  function RCButtonLink(props, ref) {

    if (props.iconOnly) {
      const {
        renderLink, iconOnly, iconLabel,
        children, hideTooltip = false,
        variant = DEFAULT_VARIANT, size = DEFAULT_SIZE, fullWidth,
        disabled, ...linkProps
      } = props as LinkElementProps & IconOnlyProps;
      const classes = buttonClassName({ variant, size, fullWidth, iconOnly: true });
      const linkEl = buildLinkElement(renderLink, <RCButtonIcon icon={iconOnly} />, ref,
        {
          className: classes,
          'aria-label': iconLabel,
          disabled,
          ...linkProps
        });
      if (hideTooltip) return linkEl;
      return (
        <RCTooltip text={children ?? iconLabel} style="buttonLabel" placement="bottom" visualOnly>
          {linkEl}
        </RCTooltip>
      );
    }

    const {
      renderLink, children, leadingIcon, trailingIcon,
      iconOnlyAt, iconLabel,
      variant = DEFAULT_VARIANT, size = DEFAULT_SIZE, fullWidth,
      disabled, ...linkProps
    } = props as LinkElementProps & StandardPropsWide;
    const { trailingIconEl, iconDropdown } = resolveTrailingIcon(trailingIcon);
    const classes = buttonClassName({
      variant, size, fullWidth,
      hasLeadingIcon: !!leadingIcon,
      hasTrailingIcon: !!trailingIconEl || iconDropdown,
      iconOnlyAt,
    });
    const content = (
      <StandardContent
        leadingIcon={leadingIcon}
        trailingIconEl={trailingIconEl}
        iconDropdown={iconDropdown}
        iconOnlyAt={iconOnlyAt}
      >
        {children}
      </StandardContent>
    );
    return buildLinkElement(renderLink, content, ref,
      {
        className: classes,
        'aria-label': iconLabel,
        disabled,
        ...linkProps
      });
  },
);

RCButtonLink.displayName = 'RCButtonLink';
